import { useMemo, useState } from 'react';
import { Input } from '@atoms/Input';
import { filterOrdersByDate, sortOrders } from '@utils/orderFilters';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Spinner } from '@atoms/Spinner';
import { OrderChatPanel } from '@/components/messaging/OrderChatPanel';
import { ReviewForm } from '@/components/review/ReviewForm';
import { orderService } from '@services/orderService';
import { formatCurrency, formatDate, ORDER_STATUS_LABELS } from '@utils/formatters';
import { useOrderDetailStore } from '@store/uiStore';
import type { Order, OrderStatus } from '@/types';

const STATUS_TABS: { id: 'all' | OrderStatus; label: string }[] = [
  { id: 'all', label: 'Tümü' },
  { id: 'pending', label: 'Beklemede' },
  { id: 'approved', label: 'Onaylandı' },
  { id: 'completed', label: 'Tamamlandı' },
  { id: 'cancelled', label: 'İptal' },
];

export function OrdersSection() {
  const openOrderDetail = useOrderDetailStore((s) => s.openOrderDetail);
  const [openChatOrderId, setOpenChatOrderId] = useState<string | null>(null);
  const [reviewOrderId, setReviewOrderId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'total-desc' | 'total-asc'>('date-desc');

  const { data: orders, isLoading } = useQuery({
    queryKey: ['restaurant', 'orders'],
    queryFn: () => orderService.getOrders(),
    refetchInterval: 30000,
  });

  const filtered = useMemo(() => {
    const byStatus = (orders ?? []).filter((o) => statusFilter === 'all' || o.status === statusFilter);
    return sortOrders(filterOrdersByDate(byStatus, dateFrom || undefined, dateTo || undefined), sortBy);
  }, [orders, statusFilter, dateFrom, dateTo, sortBy]);

  const runningTotal = useMemo(
    () => filtered.reduce((sum, o) => sum + Number(o.totalPrice), 0),
    [filtered],
  );

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Siparişlerim</h2>
        {filtered.length > 0 && (
          <span className="text-sm text-gray-600">
            Toplam: <strong>{formatCurrency(runningTotal)}</strong>
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setStatusFilter(tab.id)}
            className={`rounded-full px-3 py-1 text-sm ${
              statusFilter === tab.id ? 'bg-brand-500 text-white' : 'bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <Input label="Başlangıç" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input label="Bitiş" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        <label className="block text-sm md:col-span-2">
          <span className="mb-1 block font-medium text-gray-700">Sıralama</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          >
            <option value="date-desc">Tarih (yeni → eski)</option>
            <option value="date-asc">Tarih (eski → yeni)</option>
            <option value="total-desc">Tutar (yüksek → düşük)</option>
            <option value="total-asc">Tutar (düşük → yüksek)</option>
          </select>
        </label>
      </div>

      {!filtered.length ? (
        <Card className="text-center text-gray-500">Bu filtrede sipariş yok</Card>
      ) : (
        filtered.map((order: Order) => (
          <Card key={order.id}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold">{order.wholesaler?.name ?? 'Toptancı'}</p>
                <p className="text-sm text-gray-500">{formatDate(order.createdAt)}</p>
              </div>
              <Badge>{ORDER_STATUS_LABELS[order.status]}</Badge>
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {order.items?.map((item) => (
                <li key={item.id ?? item.productId} className="flex justify-between">
                  <span>{item.productName} x{item.quantity}</span>
                  <span>{formatCurrency(item.lineTotal)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
              <span className="font-bold text-brand-600">{formatCurrency(order.totalPrice)}</span>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => openOrderDetail(order)}>
                  Detay
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setOpenChatOrderId((prev) => (prev === order.id ? null : order.id))
                  }
                >
                  Mesaj
                </Button>
                {order.status === 'completed' && !order.hasReview && reviewOrderId !== order.id && (
                  <Button size="sm" onClick={() => setReviewOrderId(order.id)}>
                    Değerlendir
                  </Button>
                )}
                {order.hasReview && order.review && (
                  <span className="text-sm text-gray-500">
                    ⭐ {(
                      (order.review.ratingSpeed +
                        order.review.ratingQuality +
                        order.review.ratingFlavor) /
                      3
                    ).toFixed(1)}
                  </span>
                )}
              </div>
            </div>
            {openChatOrderId === order.id && <OrderChatPanel order={order} />}
            {reviewOrderId === order.id && (
              <ReviewForm order={order} onDone={() => setReviewOrderId(null)} />
            )}
          </Card>
        ))
      )}
    </div>
  );
}
